import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import JsonObjectViewer from 'app/components/ReusableUIComponents/LowLevelComponents/JsonObjectViewer';
import type {
  AutomationNeedingAttentionResolveBody,
  AutomationNeedingAttentionRerun,
  AutomationNeedingAttentionSegmentResult,
} from 'app/pages/HumanInTheLoopPage/apiService/humanInTheLoopApiService';

type SegmentResolveResponseModalProps = {
  open: boolean;
  onClose: () => void;
  body: AutomationNeedingAttentionResolveBody | null;
  showProgress: boolean;
  progressCurrent: number;
  progressTotal: number;
  progressPercentage: number;
  latestEventLabel?: string;
};

function toLabel(input: string): string {
  return input
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatValue(value: unknown): string {
  if (value == null) return '-';
  if (typeof value === 'string') return value.trim() || '-';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '-';
}

function renderObjectRows(payload: Record<string, unknown>) {
  const entries = Object.entries(payload).filter(([, value]) => {
    if (value == null) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    return true;
  });

  if (!entries.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No additional fields available.
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      {entries.map(([key, value]) => (
        <Paper key={key} variant="outlined" sx={{ p: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            {toLabel(key)}
          </Typography>
          {value != null && typeof value === 'object' ? (
            <Box sx={{ mt: 0.5 }}>
              <JsonObjectViewer
                data={value}
                defaultExpandedDepth={2}
                defaultWidth="auto"
                defaultHeight="auto"
                minWidth={260}
              />
            </Box>
          ) : (
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              {formatValue(value)}
            </Typography>
          )}
        </Paper>
      ))}
    </Stack>
  );
}

function RerunCard({ rerun, index }: { rerun: AutomationNeedingAttentionRerun; index: number }) {
  const itemCount = Array.isArray(rerun.items) ? rerun.items.length : 0;
  const taskCount = Array.isArray(rerun.task_ids) ? rerun.task_ids.length : 0;
  const opportunityCount = Array.isArray(rerun.opportunity_ids) ? rerun.opportunity_ids.length : 0;

  const rerunMeta: Record<string, unknown> = {
    test_automation_id: rerun.test_automation_id,
    task_ids: rerun.task_ids,
    opportunity_ids: rerun.opportunity_ids,
    websocket: rerun.websocket,
  };

  return (
    <Accordion disableGutters sx={{ borderRadius: 1.5, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Chip size="small" label={`Rerun ${index + 1}`} color="primary" />
          {rerun.test_automation_id ? (
            <Chip size="small" variant="outlined" label={rerun.test_automation_id} />
          ) : null}
          <Chip size="small" label={`Items: ${itemCount}`} />
          <Chip size="small" label={`Tasks: ${taskCount}`} />
          <Chip size="small" label={`Opportunities: ${opportunityCount}`} />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1.5}>
          {renderObjectRows(rerunMeta)}
          {itemCount > 0 ? (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1, fontWeight: 600 }}
              >
                Items
              </Typography>
              <Stack spacing={1}>
                {rerun.items!.map((item, idx) => (
                  <Paper key={item.task_id || `${idx}`} variant="outlined" sx={{ p: 1.25 }}>
                    {renderObjectRows(item as Record<string, unknown>)}
                  </Paper>
                ))}
              </Stack>
            </Box>
          ) : null}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default function SegmentResolveResponseModal({
  open,
  onClose,
  body,
  showProgress,
  progressCurrent,
  progressTotal,
  progressPercentage,
  latestEventLabel,
}: SegmentResolveResponseModalProps) {
  const failures = Array.isArray(body?.failures) ? body!.failures : [];
  const reruns = Array.isArray(body?.reruns) ? body!.reruns : [];
  const segmentResults = Array.isArray(body?.segment_results)
    ? (body!.segment_results as AutomationNeedingAttentionSegmentResult[])
    : [];
  const hasBatchSummary =
    typeof body?.total_segments === 'number' && body.total_segments > 0;
  const rerunCount = reruns.length;
  const progressDenominator =
    rerunCount > 0 ? rerunCount : Math.max(progressTotal, 1);
  const progressNumerator = Math.min(
    Math.max(progressCurrent, 0),
    progressDenominator,
  );
  const fixedProgressPercentage =
    progressDenominator > 0
      ? Math.round((progressNumerator / progressDenominator) * 100)
      : progressPercentage;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        Segment resolve details
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {hasBatchSummary ? (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                size="small"
                label={`Total segments: ${body?.total_segments ?? 0}`}
              />
              <Chip
                size="small"
                color="success"
                label={`Succeeded: ${body?.succeeded_segments ?? 0}`}
              />
              <Chip
                size="small"
                color="error"
                label={`Failed: ${body?.failed_segments ?? 0}`}
              />
              <Chip
                size="small"
                label={`Rescheduled tasks: ${body?.rescheduled_count ?? 0}`}
              />
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip size="small" label={`Failures: ${failures.length}`} color="error" />
              <Chip size="small" label={`Reruns: ${reruns.length}`} color="primary" />
            </Stack>
          )}

          {segmentResults.length > 0 ? (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Segment results
              </Typography>
              <Stack spacing={1}>
                {segmentResults.map((segment, idx) => (
                  <Paper
                    key={`${segment.segment_name}-${segment.reason_code}-${idx}`}
                    variant="outlined"
                    sx={{ p: 1.25 }}
                  >
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip
                          size="small"
                          color={segment.success ? 'success' : 'error'}
                          label={segment.success ? 'Success' : 'Failed'}
                        />
                        <Chip size="small" variant="outlined" label={segment.segment_name} />
                        <Chip size="small" variant="outlined" label={segment.reason_code} />
                        {segment.success ? (
                          <Chip
                            size="small"
                            label={`Rescheduled: ${segment.rescheduled_count ?? 0}`}
                          />
                        ) : null}
                      </Stack>
                      {!segment.success && segment.error ? (
                        <Typography variant="body2" color="error.main">
                          {segment.error}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Box>
          ) : null}

          {hasBatchSummary ? (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip size="small" label={`Task failures: ${failures.length}`} color="error" />
              <Chip size="small" label={`Reruns: ${reruns.length}`} color="primary" />
            </Stack>
          ) : null}

          {showProgress ? (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Resolve progress
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                {progressNumerator}/{progressDenominator}
                {latestEventLabel ? ` - Latest: ${latestEventLabel}` : ''}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={fixedProgressPercentage}
                sx={{ height: 7, borderRadius: 999 }}
              />
            </Paper>
          ) : null}

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Failures
            </Typography>
            {failures.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 1.5 }}>
                No failures were returned by resolve API.
              </Alert>
            ) : (
              <Stack spacing={1}>
                {failures.map((failure, idx) => (
                  <Paper key={failure.task_id || `${idx}`} variant="outlined" sx={{ p: 1.25 }}>
                    <Stack spacing={1}>
                      <Chip size="small" color="error" label={`Failure ${idx + 1}`} sx={{ width: 'fit-content' }} />
                      {renderObjectRows(failure as Record<string, unknown>)}
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Reruns
            </Typography>
            {reruns.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 1.5 }}>
                No reruns were returned by resolve API.
              </Alert>
            ) : (
              <Stack spacing={1}>
                {reruns.map((rerun, idx) => (
                  <RerunCard key={rerun.test_automation_id || `${idx}`} rerun={rerun} index={idx} />
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
